<template>
  <div
    class="fd-col-item"
    :class="{ 'fd-col-item--new': column._new }"
  >
    <span class="fd-col-drag">
      <feather-icon
        icon="MenuIcon"
        size="10"
      />
    </span>

    <!-- Type selector -->
    <select
      :value="column.type"
      class="fd-col-type-sel"
      @change="update({ type: $event.target.value })"
    >
      <option value="data">
        Поле
      </option>
      <option value="aggr_sum">
        Σ Итог
      </option>
      <option value="scalar">
        Скаляр
      </option>
      <option value="spacer">
        Пробел
      </option>
    </select>

    <!-- Name / Entity display -->
    <span
      v-if="column.type === 'spacer'"
      class="fd-col-name fd-col-name--dim"
    >(пробел)</span>
    <span
      v-else
      class="fd-col-name"
      :title="entityTooltip"
    >
      {{ displayName }}
    </span>

    <!-- For aggr_sum: which field to sum -->
    <template v-if="column.type === 'aggr_sum'">
      <select
        class="fd-col-dest-sel"
        :value="column.destinationEntityId"
        title="Поле для суммирования"
        @change="update({ destinationEntityId: Number($event.target.value) })"
      >
        <option :value="null">
          — поле —
        </option>
        <option
          v-for="f in numericFields"
          :key="f.entityId"
          :value="f.entityId"
        >
          {{ f.name }}
        </option>
      </select>
    </template>

    <!-- For scalar: scalar ID -->
    <template v-if="column.type === 'scalar'">
      <input
        class="fd-col-scalar-input"
        type="number"
        :value="column.scalarId"
        placeholder="ID скаляра"
        title="ID скаляра"
        @change="update({ scalarId: Number($event.target.value) })"
      >
    </template>

    <!-- Editable checkbox (record level) -->
    <label
      v-if="isRecordLevel && column.type === 'data'"
      class="fd-col-edit-label"
      title="Разрешить редактирование"
    >
      <input
        type="checkbox"
        :checked="column.editable"
        @change="update({ editable: $event.target.checked })"
      >
      <span>ред.</span>
    </label>

    <!-- Align -->
    <select
      v-if="column.type !== 'spacer'"
      :value="column.align || 'left'"
      class="fd-col-align-sel"
      @change="update({ align: $event.target.value })"
    >
      <option value="left">
        ←
      </option>
      <option value="right">
        →
      </option>
    </select>

    <!-- Remove -->
    <span
      class="fd-col-remove"
      title="Удалить колонку"
      @click="$emit('remove')"
    >✕</span>
  </div>
</template>

<script>
export default {
  name: 'CbsFormDesignerColumn',
  props: {
    column: { type: Object, required: true },
    index: { type: Number, required: true },
    isRecordLevel: { type: Boolean, default: false },
    entityMap: { type: Object, default: () => ({}) },
    paletteFields: { type: Array, default: () => [] },
  },
  computed: {
    displayName() {
      if (this.column.type === 'spacer') return ''
      const eid = this.column.entityId || this.column.destinationEntityId
      if (!eid) return '(не задано)'
      // Try entityMap first
      const info = this.entityMap[eid]
      if (info && info.name) return info.name
      // Try palette
      const pf = this.paletteFields.find(f => f.entityId === eid)
      if (pf) return pf.name
      return `#${eid}`
    },
    entityTooltip() {
      const eid = this.column.entityId || this.column.destinationEntityId
      if (!eid) return ''
      const info = this.entityMap[eid]
      const sid = info ? info.sid : ''
      return `${this.displayName} (ID: ${eid}${sid ? `, ${sid}` : ''})`
    },
    numericFields() {
      return this.paletteFields.filter(f => ['double', 'int', 'money', 'decimal'].includes(f.datatype))
    },
  },
  methods: {
    update(changes) {
      this.$emit('update', { index: this.index, changes })
    },
  },
}
</script>

<style scoped>
.fd-col-item {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 6px;
  background: #f8f8f8;
  border: 1px solid #eee;
  border-radius: 4px;
  font-size: 12px;
  transition: background 0.15s;
}
.fd-col-item:hover {
  background: #f0f0f0;
  border-color: #ddd;
}
.fd-col-drag {
  cursor: grab;
  color: #ccc;
  flex-shrink: 0;
}
.fd-col-type-sel {
  font-size: 10px;
  padding: 1px 2px;
  border: 1px solid #ddd;
  border-radius: 3px;
  background: #fff;
  min-width: 60px;
  flex-shrink: 0;
}
.fd-col-name {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 500;
}
.fd-col-name--dim {
  color: #bbb;
  font-weight: 400;
  font-style: italic;
}
.fd-col-dest-sel {
  font-size: 10px;
  padding: 1px 2px;
  border: 1px solid #ddd;
  border-radius: 3px;
  max-width: 130px;
  flex-shrink: 0;
}
.fd-col-scalar-input {
  width: 55px;
  font-size: 10px;
  padding: 1px 4px;
  border: 1px solid #ddd;
  border-radius: 3px;
  flex-shrink: 0;
}
.fd-col-edit-label {
  display: flex;
  align-items: center;
  gap: 2px;
  font-size: 10px;
  color: #888;
  cursor: pointer;
  flex-shrink: 0;
  white-space: nowrap;
}
.fd-col-edit-label input {
  margin: 0;
}
.fd-col-align-sel {
  font-size: 10px;
  padding: 0 2px;
  border: 1px solid #ddd;
  border-radius: 3px;
  width: 30px;
  flex-shrink: 0;
}
.fd-col-remove {
  cursor: pointer;
  color: #ccc;
  font-size: 11px;
  flex-shrink: 0;
  padding: 0 2px;
}
.fd-col-remove:hover { color: #e74c3c; }
.fd-col-item--new {
  background: #e8f5e9 !important;
  border-color: #66bb6a !important;
  animation: fd-col-new-pulse 1.5s ease-out;
}
@keyframes fd-col-new-pulse {
  0% { background: #a5d6a7; transform: scale(1.02); }
  100% { background: #e8f5e9; transform: scale(1); }
}
</style>
